import { offlineDatabase } from './offline-db';
import type { ActiveSession } from './auth';

// The public identity is embedded in both this client and its content-addressed SW import.
export async function pendingWorkerUpdateCount(userId: string): Promise<number> {
  return (
    (await offlineDatabase.mutations.filter((row) => row.userId === userId).count()) +
    (await offlineDatabase.conflicts.filter((row) => row.userId === userId).count())
  );
}
function identity(worker: ServiceWorker | null): Promise<boolean> {
  if (!worker) return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const finish = (valid: boolean) => {
      clearTimeout(timeout);
      channel.port1.close();
      channel.port2.close();
      resolve(valid);
    };
    const timeout = setTimeout(() => finish(false), 1000);
    channel.port1.onmessage = ({
      data,
    }: MessageEvent<{ buildId?: string; runtimeMode?: string }>) =>
      finish(
        data?.buildId === import.meta.env.VITE_WORKER_IDENTITY && data?.runtimeMode === 'connected',
      );
    worker.postMessage({ type: 'SQI_OEM_WORKER_IDENTITY' }, [channel.port2]);
  });
}
let handoff: Promise<void> | undefined;
export async function ensureConnectedWorker(session: ActiveSession): Promise<void> {
  if (session.offline || !navigator.onLine) throw Error('worker_handoff_online_required');
  if (!('serviceWorker' in navigator)) return;
  if (handoff) return handoff;
  handoff = (async () => {
    const container = navigator.serviceWorker;
    const registration = await container.getRegistration();
    if (!registration && !container.controller) return; // Dev / fresh network document.
    if (!registration) throw Error('worker_handoff_unavailable');
    if (await identity(container.controller)) return;
    if ((await pendingWorkerUpdateCount(session.userId)) > 0)
      throw Error('worker_handoff_pending_drafts');
    await registration.update();
    const deadline = Date.now() + 10000;
    while (!registration.waiting && Date.now() < deadline) {
      if (await identity(container.controller)) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    const waiting = registration.waiting;
    if (!waiting || !(await identity(waiting))) throw Error('worker_handoff_update_unavailable');
    // Recheck immediately before activation; never clear retained drafts or conflicts.
    if ((await pendingWorkerUpdateCount(session.userId)) > 0)
      throw Error('worker_handoff_pending_drafts');
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => finish(Error('worker_handoff_activation_failed')), 10000);
      const changed = () => {
        void identity(container.controller).then((valid) =>
          finish(valid ? undefined : Error('worker_handoff_identity_mismatch')),
        );
      };
      function finish(error?: Error) {
        clearTimeout(timeout);
        container.removeEventListener('controllerchange', changed);
        if (error) reject(error);
        else resolve();
      }
      container.addEventListener('controllerchange', changed);
      waiting.postMessage({ type: 'SKIP_WAITING' });
    });
    if (!(await identity(container.controller))) throw Error('worker_handoff_identity_mismatch');
  })();
  try {
    await handoff;
  } finally {
    handoff = undefined;
  }
}
